// @owners { team: patients-team }
import { COLORS, SPACING } from '@alto/design-library-tokens';
import {
  ActionSheetContext,
  AltoIcon,
  AltoSpinningLoader,
  Body,
  Card,
  type DateData,
  Description,
  InlineAlert,
  InputRadio,
  InputRadioGroup,
  LgPadding,
  MdPadding,
  Pressable,
  Row,
  Separator,
  Tag,
  XsSpacing,
} from '@alto/design-system';
import { ListDescription } from '@alto/design-system';
import { Experimentation } from '@alto/experimentation';
import { useIsMutating } from '@tanstack/react-query';
import React, { useCallback, useContext } from 'react';
import { updateOrder } from '~shared/actions/cart';
import { useCheckoutDateErrors } from '~shared/features/checkout/hooks/useCheckoutDateErrors';
import { useDatePickerViewedEvent } from '~shared/features/checkout/hooks/useDatePickerViewedEvent';
import { getShowingValidationErrors } from '~shared/features/checkout/selectors/getCart';
import { getOrderAddress } from '~shared/features/checkout/selectors/getOrder';
import { getIsEditingExistingOrder } from '~shared/features/ui/selectors/getCart';
import { formatDateStringForAPI } from '~shared/helpers/date';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { ORIGIN_NAMES } from '~shared/lib/analytics/src/getOrigin';
import { useQueryShipmentFeesAvailable } from '~shared/queries';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { CREATE_ORDER_MUTATION_KEY } from '../../queries/useCreateOrder';
import { FeeDetailsActionSheet } from './FeeDetailsActionSheet';
import { FeeRadioRightContent } from './FeeRadioRightContent';
import { SelectDateActionSheet } from './SelectDateActionSheet';
import { ShadowFulfillmentOptions } from './ShadowFulfillmentOptions';
import { type RadioValue } from './types';
import { useSelectDateCard } from './useSelectDateCard';
import { formValueToFeeType } from './utils';

type Props = {
  readonly openGetItSoonerFlow: () => void;
  readonly disabled?: boolean;
};

export const SelectDateCard = ({ openGetItSoonerFlow, disabled = false }: Props) => {
  const dispatch = useDispatchShared();
  const { trackEvent } = useAnalytics();
  const { setActiveActionSheet } = useContext(ActionSheetContext);
  const isEditingOrder = useSelectorShared(getIsEditingExistingOrder);
  const saveOrderLoading = useSelectorShared((state) => state.ui.loading.saveOrderLoading);
  const isCreateOrderPending = !!useIsMutating({ mutationKey: CREATE_ORDER_MUTATION_KEY });
  const showingValidationErrors = useSelectorShared(getShowingValidationErrors);
  const { dateValidationErrors } = useCheckoutDateErrors();
  const {
    checkoutDateItems,
    preSelect,
    earliestUpcomingShipment,
    hasSchedulableDeliveryFeesInCart,
    handleRadioValueChange,
    earliestAvailableDate,
    loading,
  } = useSelectDateCard();
  const { feeToDateHash } = useQueryShipmentFeesAvailable();
  const disableSelector = disabled || saveOrderLoading || loading || isCreateOrderPending;
  const showErrors = showingValidationErrors && dateValidationErrors?.length;
  const errorMessage = dateValidationErrors.map((e) => e.message).join('. ');
  const address = useSelectorShared(getOrderAddress);
  const { value: isShadowFulfillmentOptionsEnabled } = Experimentation.useFeatureFlag('shadow_fulfillment_options');

  useDatePickerViewedEvent();

  const handleSelectCustomDate = useCallback(
    ({ dateString }: DateData) => {
      handleRadioValueChange('any-day-delivery');
      dispatch(updateOrder({ date: formatDateStringForAPI(dateString) }));
    },
    [dispatch, handleRadioValueChange],
  );

  const handleRadioPress = useCallback(
    (value: string) => {
      handleRadioValueChange(value as RadioValue);
      trackEvent({
        event: value === 'same-day-delivery' ? EVENTS.SAME_DAY_DELIVERY_FEE_SELECTED : EVENTS.SELECT_OTHER_DATE_TAPPED,
        params: { origin: isEditingOrder ? ORIGIN_NAMES.EDIT_DELIVERY : ORIGIN_NAMES.CHECKOUT },
      });
      const feeType = formValueToFeeType[value];
      const dateFromFee = feeToDateHash[feeType];

      if (value === 'earliest-available-date') {
        dispatch(updateOrder({ date: formatDateStringForAPI(earliestAvailableDate) }));
        return;
      }

      if ((value === 'same-day-order-bundling' || value === 'order-bundling') && earliestUpcomingShipment) {
        dispatch(updateOrder({ date: formatDateStringForAPI(earliestUpcomingShipment.date), tip_amount: undefined }));
        return;
      }

      if (dateFromFee && feeType !== 'standard_delivery') {
        dispatch(updateOrder({ date: formatDateStringForAPI(dateFromFee) }));
        return;
      }

      setActiveActionSheet(
        <SelectDateActionSheet
          handleSelectCustomDate={handleSelectCustomDate}
          openGetItSoonerFlow={openGetItSoonerFlow}
        />,
      );
    },
    [
      trackEvent,
      isEditingOrder,
      setActiveActionSheet,
      handleSelectCustomDate,
      handleRadioValueChange,
      openGetItSoonerFlow,
      dispatch,
      feeToDateHash,
      earliestUpcomingShipment,
      earliestAvailableDate,
    ],
  );

  const handleFeeDetailsPress = useCallback(() => {
    setActiveActionSheet(<FeeDetailsActionSheet />);
  }, [setActiveActionSheet]);

  if (loading) {
    return (
      <Card
        title="Select date"
        overflow="hidden"
      >
        <Row centerHorizontally>
          <LgPadding topPadding={SPACING.STATIC.NONE}>
            <AltoSpinningLoader />
          </LgPadding>
        </Row>
      </Card>
    );
  }

  const rightContent = hasSchedulableDeliveryFeesInCart ? (
    <Pressable onPress={handleFeeDetailsPress}>
      <Description color={COLORS.TEXT_COLORS.PRIMARY}>Fee details</Description>
    </Pressable>
  ) : undefined;

  return (
    <Card
      title="Select date"
      overflow="hidden"
      RightContent={rightContent}
    >
      <InputRadioGroup
        onValueChange={handleRadioPress}
        defaultValue={preSelect}
        disabled={disableSelector}
      >
        {(checkoutDateItems || []).map((checkoutDateItem) => {
          const { label, type, feeAmountLabel, feeOriginalAmountLabel, tag, description } = checkoutDateItem;
          return (
            <React.Fragment key={`radio-${type}`}>
              <Separator />
              <InputRadio
                disabled={disableSelector}
                radioPlacement="left"
                label={label}
                value={type}
                testID={type}
                descriptions={description ? [<ListDescription key={description}>{description}</ListDescription>] : []}
                tag={
                  tag === 'expedited' ? (
                    <Tag
                      icon={<AltoIcon name="lightning-small" />}
                      type="recommended"
                      key="expedited"
                      label="expedited"
                    />
                  ) : undefined
                }
                RightContent={
                  <FeeRadioRightContent
                    feeAmountLabel={feeAmountLabel}
                    feeOriginalAmountLabel={feeOriginalAmountLabel}
                    loading={loading}
                    showIconOpenCalendar={type === 'any-day-delivery'}
                  />
                }
              />
            </React.Fragment>
          );
        })}
      </InputRadioGroup>
      {showErrors ? (
        <MdPadding>
          <XsSpacing />
          <InlineAlert type="error">
            <Body>{errorMessage}</Body>
          </InlineAlert>
        </MdPadding>
      ) : null}
      {address && isShadowFulfillmentOptionsEnabled && (
        <ShadowFulfillmentOptions
          address={address}
          checkoutDateItems={checkoutDateItems}
        />
      )}
    </Card>
  );
};
